import { observer } from 'mobx-react';
import * as React from 'react';

import { Selector } from 'shared/newComponents/form';
import {
  SelectorOptions,
  SelectorSize,
  SelectorTriggerSize,
  SelectorType
} from 'shared/newEntities/components/Selector';
import loadGlobalStoresHOC from 'shared/HOCs/loadGlobalStoresHOC';
import {
  useCabinetsStore,
  useCabinetStore,
  useRouterStore,
  useUiStore
} from 'stores/index';
import { TranslationString } from 'shared/entities/localization';
import { useModalState } from 'shared/entities/common/hooks';
import { PlusIcon, SettingsIcon } from 'shared/newComponents/icons';
import { urls } from 'shared/entities/domain';
import { TypographySize } from 'shared/newEntities/components/Typography';
import { ComponentType } from 'shared/entities/components/Component';
import CabinetCreatingModal from 'shared/newContainers/modals/CabinetCreatingModal';

type Props = {
  value: string | null;
  onChange: (value: string) => void;
  withLabel?: boolean;
  label?: TranslationString | string;
  placeholder?: TranslationString;
  labelSize?: TypographySize.s | TypographySize.m;
  triggerSize?: SelectorTriggerSize;
  size?: SelectorSize;
};

const CabinetSelector: React.FC<Props> = (props: Props) => {
  const {
    onChange,
    value,
    placeholder,
    withLabel = true,
    label,
    labelSize,
    triggerSize,
    size
  } = props;
  const modalState = useModalState();
  const cabinetsStore = useCabinetsStore();
  const cabinetStore = useCabinetStore();
  const routerStore = useRouterStore();
  const uiStore = useUiStore();

  const handleOpenCabinetSettings = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (uiStore.isMobileSize) {
        uiStore.menuState.close();
      }
    },
    [uiStore.isMobileSize]
  );

  const handleChangeCabinet = React.useCallback((e) => {
    e.stopPropagation();
    routerStore.removeOpeningParams();
  }, []);

  const options = React.useMemo((): SelectorOptions => {
    const cabinets = cabinetsStore.cabinets.items;
    if (
      cabinetStore.entity &&
      !cabinetsStore.cabinets.items.find(
        (cabinet) => cabinet.domain === cabinetStore.entity?.domain
      )
    ) {
      cabinets.unshift(cabinetStore.entity);
    }

    return cabinets.reduce<SelectorOptions>(
      (acc, cabinet) => {
        const isCurCabinet = cabinetStore.entity?.domain === cabinet.domain;
        return {
          ...acc,
          entities: {
            ...acc.entities,
            [cabinet.domain]: {
              id: cabinet.domain,
              title: cabinet.name,
              rightComponent: {
                Icon: SettingsIcon,
                action: isCurCabinet
                  ? {
                      type: ComponentType.link,
                      to: urls.SETTINGS.root,
                      onClick: handleOpenCabinetSettings
                    }
                  : {
                      type: ComponentType.a,
                      href: routerStore.getUrl({
                        subDomain: cabinet.domain,
                        path: urls.SETTINGS.root
                      }),
                      onClick: handleChangeCabinet
                    },
                reference: (t) =>
                  t('cabinetSelector.iconReference', { ns: 'selectors' })
              }
            }
          },
          keys: [...acc.keys, cabinet.domain]
        };
      },
      { keys: [], entities: {} }
    );
  }, [
    cabinetStore.entity,
    cabinetStore.entity?.domain,
    cabinetsStore.cabinets.items,
    handleChangeCabinet,
    handleOpenCabinetSettings
  ]);

  const actualLabel = React.useMemo((): TranslationString | string | null => {
    if (label) {
      return label;
    }

    if (!withLabel) {
      return null;
    }

    return (t) => t('cabinetSelector.label', { ns: 'selectors' });
  }, [label, withLabel]);

  return (
    <>
      <CabinetCreatingModal
        opened={modalState.opened}
        onClose={modalState.close}
      />
      <Selector
        label={actualLabel}
        topItem={{
          Icon: PlusIcon,
          onClick: modalState.open,
          title: (t) => t('cabinetSelector.topItem.title', { ns: 'selectors' })
        }}
        options={options}
        placeholder={placeholder}
        type={SelectorType.single}
        value={value}
        onChange={onChange}
        loadingStage={cabinetsStore.loadingStage}
        withRemoving={false}
        sortOrder={null}
        labelSize={labelSize}
        triggerSize={triggerSize}
        size={size}
      />
    </>
  );
};

export default loadGlobalStoresHOC(observer(CabinetSelector), [
  'cabinetsStore'
]);
